/**
 * Scoped share links: create a link that shows ONLY the items you pick,
 * optionally expiring; list your links; revoke any of them.
 *
 * A link works even while your profile is private — that is the point: a
 * deliberate, limited disclosure. The full URL is shown once, right after
 * creation (the server keeps only a hash of it), so copy it then.
 */

import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { mutationOutcome } from '../../utils/mutationOutcome';
import { useGetMyHighlightsQuery } from '../../store/api/portfolioApi';
import { useGetMyVerifiedCredentialsQuery } from '../../store/api/credentialsApi';
import {
  useCreateShareLinkMutation,
  useGetMyShareLinksQuery,
  useRevokeShareLinkMutation,
  type ShareItemRef,
  type ShareLinkSummary,
} from '../../store/api/sharingApi';

const EXPIRY_OPTIONS = [
  { label: 'Never', days: 0 },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
] as const;

const keyOf = (i: ShareItemRef) => (i.id ? `${i.kind}:${i.id}` : i.kind);

function describeItems(items: ShareItemRef[]): string {
  const n = (k: ShareItemRef['kind']) => items.filter((i) => i.kind === k).length;
  const parts: string[] = [];
  if (n('profile_header')) parts.push('name & headline');
  if (n('highlight')) parts.push(`${n('highlight')} highlight${n('highlight') > 1 ? 's' : ''}`);
  if (n('credential')) parts.push(`${n('credential')} credential${n('credential') > 1 ? 's' : ''}`);
  if (n('authority_summary')) parts.push('authority summary');
  return parts.join(', ') || 'no items';
}

export function ShareLinksManager() {
  const { data: highlights = [] } = useGetMyHighlightsQuery();
  const { data: credentials = [] } = useGetMyVerifiedCredentialsQuery();
  const { data: links = [], isLoading: linksLoading } = useGetMyShareLinksQuery();
  const [createLink, { isLoading: creating }] = useCreateShareLinkMutation();
  const [revokeLink] = useRevokeShareLinkMutation();

  const [selected, setSelected] = useState<Record<string, ShareItemRef>>({ profile_header: { kind: 'profile_header' } });
  const [expiryDays, setExpiryDays] = useState<number>(30);
  const [created, setCreated] = useState<{ url: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A revoke the server refused: the link is still live, and the user is told. */
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const handleRevoke = async (id: string) => {
    setRevokeError(null);
    await mutationOutcome(revokeLink(id), 'The link could not be revoked. It is still active — try again.', setRevokeError);
  };

  const activeCredentials = useMemo(() => credentials.filter((c) => c.isActive), [credentials]);

  const toggle = (item: ShareItemRef) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[keyOf(item)]) delete next[keyOf(item)];
      else next[keyOf(item)] = item;
      return next;
    });

  const chosen = Object.values(selected);
  const hasContent = chosen.some((i) => i.kind !== 'profile_header');

  const handleCreate = async () => {
    setError(null);
    setCreated(null);
    try {
      const expiresAt = expiryDays > 0 ? new Date(Date.now() + expiryDays * 86_400_000).toISOString() : null;
      const link = await createLink({ items: chosen, expiresAt }).unwrap();
      setCreated({ url: `${window.location.origin}${link.path}` });
      setCopied(false);
    } catch (e: any) {
      setError(e?.data?.message || 'Could not create the link.');
    }
  };

  const handleCopy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Copy failed — select the link and copy it manually.');
    }
  };

  const itemBox = (item: ShareItemRef, label: string, hint?: string) => (
    <label key={keyOf(item)} className="flex items-start gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
      <input type="checkbox" className="mt-1" checked={!!selected[keyOf(item)]} onChange={() => toggle(item)} />
      <span>
        <span className="text-sm text-gray-900">{label}</span>
        {hint && <span className="block text-xs text-gray-500">{hint}</span>}
      </span>
    </label>
  );

  return (
    <div className="space-y-5" data-testid="share-links-manager">
      <div>
        <h3 className="font-semibold text-gray-900">Private share links</h3>
        <p className="text-sm text-gray-500 mt-1">
          Share only what you choose — even while your profile is private. Anyone with the link sees
          these items and nothing else. You can revoke a link at any time.
        </p>
      </div>

      <div className="border rounded-lg divide-y">
        <div className="p-3">
          <p className="text-xs font-medium uppercase text-gray-500 mb-1">Profile</p>
          {itemBox({ kind: 'profile_header' }, 'Display name & headline')}
          {itemBox({ kind: 'authority_summary' }, 'Authority summary', 'Tier, score and session count')}
        </div>
        <div className="p-3">
          <p className="text-xs font-medium uppercase text-gray-500 mb-1">Portfolio highlights</p>
          {highlights.length === 0 && <p className="text-sm text-gray-400 px-2">No highlights yet.</p>}
          {highlights.map((h) => itemBox({ kind: 'highlight', id: h.id }, h.title, h.description))}
        </div>
        <div className="p-3">
          <p className="text-xs font-medium uppercase text-gray-500 mb-1">Verified credentials</p>
          {activeCredentials.length === 0 && <p className="text-sm text-gray-400 px-2">No active credentials.</p>}
          {activeCredentials.map((c) => itemBox({ kind: 'credential', id: c.id }, c.domainLabel, c.category))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-700 flex items-center gap-2">
          Expires
          <select className="input py-1" value={expiryDays} onChange={(e) => setExpiryDays(Number(e.target.value))}>
            {EXPIRY_OPTIONS.map((o) => (
              <option key={o.days} value={o.days}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <button className="btn btn-primary" disabled={creating || !hasContent} onClick={handleCreate}>
          {creating ? 'Creating…' : 'Create link'}
        </button>
        {!hasContent && <span className="text-xs text-gray-400">Pick at least one highlight, credential or summary.</span>}
      </div>

      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}

      {created && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2" data-testid="share-link-created">
          <p className="text-sm text-green-800 font-medium">Link created. Copy it now — it will not be shown again.</p>
          <div className="flex gap-2">
            <input readOnly value={created.url} className="input flex-1 text-sm" onFocus={(e) => e.target.select()} />
            <button
              onClick={handleCopy}
              className={clsx('btn min-w-[90px]', copied ? 'bg-green-500 text-white' : 'btn-primary')}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Your links</h4>
        {revokeError && (
          <p className="text-sm text-red-600 mb-2" role="alert" data-testid="share-link-revoke-error">{revokeError}</p>
        )}
        {linksLoading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : links.length === 0 ? (
          <p className="text-sm text-gray-400">No links yet.</p>
        ) : (
          <ul className="divide-y border rounded-lg">
            {links.map((l: ShareLinkSummary) => (
              <li key={l.id} className="flex items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                  <p className="text-sm text-gray-900 truncate">{describeItems(l.items)}</p>
                  <p className="text-xs text-gray-500">
                    Created {new Date(l.createdAt).toLocaleDateString()} · {l.viewCount} view{l.viewCount === 1 ? '' : 's'}
                    {l.expiresAt && ` · expires ${new Date(l.expiresAt).toLocaleDateString()}`}
                  </p>
                </div>
                {l.status === 'active' ? (
                  <button
                    className="btn btn-outline text-red-600 border-red-300 hover:bg-red-50 text-sm"
                    onClick={() => void handleRevoke(l.id)}
                  >
                    Revoke
                  </button>
                ) : (
                  <span className="text-xs text-gray-400 capitalize">{l.status}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
